/**
 * User-aware "today" helpers.
 *
 * Every "now" / "today" used for budget cycles, scheduled bills, and urgency
 * must route through here so the math follows the user's IANA time zone (which
 * they can change in Settings, and which travels with them).
 *
 * `nowInTz(tz)` returns a JS Date whose wall-clock fields (year/month/date/
 * hours/minutes/seconds) read like local time in `tz`. We can then pass it
 * straight to existing helpers that read those fields (`getBudgetPeriod`,
 * `getUrgentBills`, `toLocalDateStr`).
 */

export const DEFAULT_TZ = 'Asia/Kuwait';

let cachedDetected: string | null = null;
export function detectBrowserTimezone(): string {
  if (cachedDetected) return cachedDetected;
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    cachedDetected = tz && tz.length > 0 ? tz : DEFAULT_TZ;
  } catch {
    cachedDetected = DEFAULT_TZ;
  }
  return cachedDetected;
}

/**
 * Return a Date whose wall-clock fields match the current time in `tz`.
 * The underlying epoch is NOT the real epoch - never use .getTime() on this
 * value for elapsed-time math. Use it only for cycle/today derivations that
 * read year/month/date components.
 */
export function nowInTz(tz: string | null | undefined = DEFAULT_TZ): Date {
  const zone = tz && tz.length > 0 ? tz : DEFAULT_TZ;
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).formatToParts(new Date());
    const get = (t: string) => Number(parts.find((p) => p.type === t)?.value);
    const hour = get('hour');
    return new Date(
      get('year'),
      get('month') - 1,
      get('day'),
      hour === 24 ? 0 : hour,
      get('minute'),
      get('second'),
    );
  } catch {
    return new Date();
  }
}

/** yyyy-MM-dd as it reads on the user's clock in `tz`. */
export function todayStrInTz(tz: string | null | undefined = DEFAULT_TZ): string {
  const d = nowInTz(tz);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/** localStorage key where the user's chosen IANA zone is cached (see useUserTimezone). */
export const USER_TZ_STORAGE_KEY = 'fennest:user-tz';

/**
 * The timezone we should use for any write that records a calendar day:
 * the user's saved preference first, then the device zone.
 * Safe to call from non-React modules.
 */
export function preferredTimezone(): string {
  try {
    const v = localStorage.getItem(USER_TZ_STORAGE_KEY);
    if (v && v.length > 0) return v;
  } catch {
    /* ignore */
  }
  return detectBrowserTimezone();
}

/**
 * "Today" as yyyy-MM-dd in the user's timezone. This is the ONLY value that
 * should be sent as expense_date / effective_date / received_date when the
 * user is recording something "now". Never derive it from toISOString().
 */
export function todayForUser(): string {
  return todayStrInTz(preferredTimezone());
}

/** Format a Date as yyyy-MM-dd using its own local fields (no UTC shift). */
export function dateStrLocal(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}
